import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/types";

export type SandboxProvision = components["schemas"]["SandboxProvisionRead"];
export type SandboxDeleteResult = components["schemas"]["SandboxDeleteResult"];

/**
 * The POST /v1/sandbox/generate contract (RES-152). The logo travels as
 * base64 PNG bytes in the JSON body — the server re-encodes with Pillow
 * regardless (RES-159), so no multipart is needed. The result's
 * temp_password is shown exactly once, never persisted client-side.
 */
export type SandboxGenerateRequest =
  components["schemas"]["SandboxGenerateRequest"];
export type SandboxGenerateResult =
  components["schemas"]["SandboxGenerateResult"];

export function useSandboxes() {
  return useQuery({
    queryKey: ["sandboxes"],
    queryFn: () => api.get<SandboxProvision[]>("/sandbox"),
  });
}

export function useGenerateSandbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SandboxGenerateRequest) =>
      api.post<SandboxGenerateResult>("/sandbox/generate", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sandboxes"] }),
  });
}

export function useDeleteSandbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (departmentId: string) =>
      api.delete<SandboxDeleteResult>(`/sandbox/${departmentId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sandboxes"] }),
  });
}
